import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Users, GraduationCap, Camera, ArrowRight, Trophy, MapPin, Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { recordFor, formatDate } from "@/data/content";
import { youtubeThumb, youtubeEmbed } from "@/utils/youtube";

const QUICK_LINKS = [
  { to: "/schedule", icon: Calendar, title: "Schedule & Results", text: "Game dates, times, opponents, and final scores.", testid: "quick-link-schedule" },
  { to: "/roster", icon: Users, title: "Team Roster", text: "Meet the Bears — jersey numbers, positions, and class years.", testid: "quick-link-roster" },
  { to: "/coaches", icon: GraduationCap, title: "Coaching Staff", text: "Bios and backgrounds for Coach Mitchell and his staff.", testid: "quick-link-coaches" },
  { to: "/gallery", icon: Camera, title: "Photos & Videos", text: "Game action, highlight videos, and community moments.", testid: "quick-link-gallery" },
];

export default function Home() {
  const { site } = useSite();
  const c = site.content;
  const [playFeatured, setPlayFeatured] = useState(false);
  const { w, l } = recordFor(site.games, "Varsity");
  const played = site.games.filter((g) => g.wl === "W" || g.wl === "L");
  const lastGame = played[played.length - 1];
  const featured = site.news.find((n) => n.id === c.featured_news_id);
  const newsList = featured ? [featured, ...site.news.filter((n) => n.id !== featured.id)] : site.news;
  const featuredVideo = site.videos.find((v) => v.featured);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[88vh] flex items-end" data-testid="hero-section">
        <img src={c.hero_image} alt="Norwalk Bears team" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A3823] via-[#0F4C2A]/70 to-black/30" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-40">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-block bg-[#D97706] text-white text-xs sm:text-sm font-bold uppercase tracking-[0.25em] px-4 py-1.5 rounded-sm mb-5">
              {c.hero_kicker}
            </span>
            <h1 className="font-condensed text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tight leading-[0.9] text-white whitespace-pre-line" data-testid="hero-headline">
              {c.hero_title}
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-white/85 font-medium leading-relaxed">{c.hero_subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/schedule" data-testid="hero-cta-schedule" className="bg-[#D97706] hover:bg-[#F59E0B] text-white px-7 py-3.5 rounded-md font-bold text-sm uppercase tracking-wide transition-colors shadow-lg inline-flex items-center gap-2">
                Schedule & Results <ArrowRight size={16} />
              </Link>
              <Link to="/roster" data-testid="hero-cta-roster" className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/40 text-white px-7 py-3.5 rounded-md font-bold text-sm uppercase tracking-wide transition-colors">
                Meet the Roster
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#0A3823] diagonal-stripes text-white" data-testid="season-record-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8 text-center sm:text-left">
          <Reveal>
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <Trophy className="text-[#F59E0B]" size={28} />
              <div>
                <div className="font-stat text-3xl sm:text-4xl font-bold" data-testid="record-stat">{w}-{l}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">Varsity Record</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <div className="font-condensed text-2xl sm:text-3xl font-bold uppercase" data-testid="playoff-stat">FCIAC Semifinalist</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">2026 Postseason Finish</div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex items-center justify-center sm:justify-start gap-3">
              <MapPin className="text-[#F59E0B]" size={28} />
              <div>
                <div className="font-condensed text-2xl sm:text-3xl font-bold uppercase">{c.field_name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">Home of Bears Baseball</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <img src={c.logo_url} alt="Team logo" className="w-56 sm:w-72 mx-auto drop-shadow-xl" data-testid="mission-logo" />
          </Reveal>
          <Reveal delay={0.1}>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">Our Mission</span>
            <h2 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mt-3 leading-none">
              {c.mission_title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{c.mission_text}</p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-[#0F4C2A] font-bold text-sm uppercase tracking-wide hover:text-[#D97706] transition-colors" data-testid="mission-about-link">
              More about the program <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {featuredVideo && (
        <section className="py-14 sm:py-20 bg-slate-50" data-testid="featured-video-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <Reveal>
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">Featured Video</span>
              <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mt-3 leading-none">Bears Baseball Highlights</h2>
              <p className="mt-4 font-condensed text-xl font-bold uppercase text-slate-900" data-testid="featured-video-title">{featuredVideo.title}</p>
              {featuredVideo.description && (
                <p className="mt-2 text-slate-600 leading-relaxed text-sm sm:text-base">{featuredVideo.description}</p>
              )}
              <Link to="/gallery" className="mt-6 inline-flex items-center gap-2 text-[#0F4C2A] font-bold text-sm uppercase tracking-wide hover:text-[#D97706] transition-colors" data-testid="featured-video-more-link">
                Watch more videos <ArrowRight size={16} />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              {playFeatured ? (
                <div className="aspect-video rounded-lg overflow-hidden shadow-xl border border-slate-200" data-testid="featured-video-player">
                  <iframe
                    src={youtubeEmbed(featuredVideo.video_id)}
                    title={featuredVideo.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <button
                  className="group relative block w-full aspect-video rounded-lg overflow-hidden shadow-xl border border-slate-200"
                  onClick={() => setPlayFeatured(true)}
                  data-testid="featured-video-play"
                >
                  <img src={youtubeThumb(featuredVideo.video_id)} alt={featuredVideo.title} className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="h-16 w-16 rounded-full bg-[#D97706] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play size={26} className="text-white fill-white ml-1" />
                    </span>
                  </span>
                </button>
              )}
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-14 sm:py-20 bg-slate-50" data-testid="quick-links-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mb-2">Program Hub</h2>
            <p className="text-slate-600 mb-10 text-sm sm:text-base">Everything players, parents, and fans need — one click away.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {QUICK_LINKS.map((q, i) => (
              <Reveal key={q.to} delay={i * 0.08}>
                <Link to={q.to} data-testid={q.testid} className="card-lift block bg-white rounded-lg border border-slate-200 p-6 h-full hover:border-[#0F4C2A]/40">
                  <div className="h-11 w-11 rounded-md bg-[#0F4C2A] flex items-center justify-center mb-4">
                    <q.icon size={20} className="text-[#F59E0B]" />
                  </div>
                  <h3 className="font-condensed text-xl font-bold uppercase text-slate-900">{q.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{q.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#D97706]">
                    Open <ArrowRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-white" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A]">Latest From The Program</h2>
                <p className="text-slate-600 mt-2 text-sm sm:text-base">News, highlights, and community moments.</p>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {newsList.slice(0, 3).map((n, i) => (
              <Reveal key={n.id || n.title} delay={i * 0.08}>
                <article className="card-lift bg-white rounded-lg border border-slate-200 overflow-hidden h-full" data-testid={`news-card-${i}`}>
                  {n.img && (
                    <div className="h-48 overflow-hidden">
                      <img src={n.img} alt={n.title} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-[#0F4C2A]/10 text-[#0F4C2A] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">{n.tag}</span>
                      <span className="text-xs text-slate-500">{n.date}</span>
                    </div>
                    <h3 className="font-condensed text-xl font-bold uppercase leading-tight text-slate-900">{n.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{n.text}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          {lastGame && (
            <Reveal delay={0.1}>
              <p className="mt-8 text-center text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="last-game-note">
                Latest result: {formatDate(lastGame.date)} vs {lastGame.opponent} — {lastGame.wl} {lastGame.score}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-[#0F4C2A] diagonal-stripes py-14 sm:py-20" data-testid="home-cta-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-condensed text-3xl sm:text-5xl font-black uppercase tracking-tight text-white">Questions About The Program?</h2>
            <p className="mt-4 text-white/80 max-w-xl mx-auto text-sm sm:text-base">Players, parents, and prospective Bears — {c.contact_coach} would love to hear from you.</p>
            <Link to="/contact" data-testid="cta-contact" className="mt-8 inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#F59E0B] text-white px-8 py-3.5 rounded-md font-bold text-sm uppercase tracking-wide transition-colors shadow-lg">
              Contact the Program <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
